
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Tutorial } from '@/types/tutorial';
import { Printer } from '@/hooks/use-printer-search';
import { Contributor } from '@/hooks/use-contributors';
import { supabase } from '@/integrations/supabase/client';

export const useTutorialCreator = (existingTutorial?: Tutorial) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPrinters, setSelectedPrinters] = useState<Printer[]>([]);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [tutorial, setTutorial] = useState<Partial<Tutorial>>({
    title: existingTutorial?.title || "",
    coverImage: existingTutorial?.coverImage || "",
    shortDescription: existingTutorial?.shortDescription || "",
    tags: existingTutorial?.tags || [],
    bom: existingTutorial?.bom || [],
    sections: existingTutorial?.sections || [],
    status: "draft"
  });

  const saveTutorialPrinterRelationships = async (tutorialId: string, printerIds: string[]) => {
    const { error: deleteError } = await supabase
      .from('tutorial_printers')
      .delete()
      .eq('tutorial_id', tutorialId);

    if (deleteError) {
      console.error('Error deleting existing printer relationships:', deleteError);
      throw deleteError;
    }

    if (printerIds.length > 0) {
      const relationships = printerIds
        .filter(id => !id.startsWith('pending-'))
        .map(printerId => ({
          tutorial_id: tutorialId,
          printer_id: printerId
        }));

      if (relationships.length > 0) {
        const { error: insertError } = await supabase
          .from('tutorial_printers')
          .insert(relationships);

        if (insertError) {
          console.error('Error inserting printer relationships:', insertError);
          throw insertError;
        }
      }
    }
  };

  const saveTutorialContributors = async (tutorialId: string, contributorsList: Contributor[]) => {
    const { error: deleteError } = await supabase
      .from('tutorial_contributors')
      .delete()
      .eq('tutorial_id', tutorialId);

    if (deleteError) {
      console.error('Error deleting existing contributors:', deleteError);
      throw deleteError;
    }

    if (contributorsList.length > 0) {
      const contributorRelationships = contributorsList.map(contributor => ({
        tutorial_id: tutorialId,
        user_id: contributor.id.startsWith('plain-') ? null : contributor.id, // Handle plain text contributors
        contributor_name: contributor.username, // Store the name for both types
        role: contributor.role || 'contributor',
        added_by: user?.id
      }));

      const { error: insertError } = await supabase
        .from('tutorial_contributors')
        .insert(contributorRelationships);

      if (insertError) {
        console.error('Error inserting contributors:', insertError);
        throw insertError;
      }
    }
  };

  const validateTutorial = () => {
    if (!tutorial.title?.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide a title for your tutorial.",
        variant: "destructive"
      });
      return false;
    }

    if (!tutorial.shortDescription?.trim()) {
      toast({
        title: "Missing Information", 
        description: "Please provide a description for your tutorial.",
        variant: "destructive"
      });
      return false;
    }

    if (!tutorial.sections?.length) {
      toast({
        title: "Missing Content",
        description: "Please add at least one section to your tutorial.",
        variant: "destructive"
      });
      return false;
    }

    const hasContentSections = tutorial.sections.some(section => 
      section.steps && section.steps.length > 0
    );

    if (!hasContentSections) {
      toast({
        title: "Missing Content",
        description: "Please add steps to at least one section.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSaveDraft = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to save a draft.",
        variant: "destructive"
      });
      return;
    }

    try {
      const tutorialData = {
        title: tutorial.title || "Untitled Tutorial",
        short_description: tutorial.shortDescription || "",
        description: tutorial.shortDescription || "",
        cover_image: tutorial.coverImage || "",
        tags: tutorial.tags || [],
        content: {
          bom: tutorial.bom || [],
          sections: tutorial.sections || []
        } as any,
        status: "draft",
        created_by: user.id
      };

      const { data, error } = await supabase
        .from('tutorials')
        .insert(tutorialData)
        .select()
        .single();

      if (error) throw error;

      const printerIds = selectedPrinters.map(p => p.id);
      await saveTutorialPrinterRelationships(data.id, printerIds);
      await saveTutorialContributors(data.id, contributors);

      toast({
        title: "Draft Saved",
        description: "Your tutorial has been saved as a draft.",
      });

      console.log('Draft saved:', data);
    } catch (error: any) {
      console.error('Failed to save draft:', error);
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save draft. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSubmitForReview = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to submit a tutorial.",
        variant: "destructive"
      });
      return;
    }

    if (!validateTutorial()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tutorialData = {
        title: tutorial.title!,
        short_description: tutorial.shortDescription!,
        description: tutorial.shortDescription!,
        cover_image: tutorial.coverImage || "",
        tags: tutorial.tags || [],
        content: {
          bom: tutorial.bom || [],
          sections: tutorial.sections || []
        } as any,
        status: "pending",
        created_by: user.id
      };

      console.log('Submitting tutorial:', tutorialData);

      const { data, error } = await supabase
        .from('tutorials')
        .insert(tutorialData)
        .select()
        .single();

      if (error) {
        console.error('Submission error:', error);
        throw error;
      }

      const printerIds = selectedPrinters.map(p => p.id);
      await saveTutorialPrinterRelationships(data.id, printerIds);
      await saveTutorialContributors(data.id, contributors);

      console.log('Tutorial submitted successfully:', data);

      toast({
        title: "Tutorial Submitted",
        description: "Your tutorial has been submitted for admin review.",
      });

      return true;
    } catch (error: any) {
      console.error('Failed to submit tutorial:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit tutorial. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    tutorial,
    setTutorial,
    selectedPrinters,
    setSelectedPrinters,
    contributors,
    setContributors,
    isSubmitting,
    handleSaveDraft,
    handleSubmitForReview,
    user
  };
};
